import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { CheckCircle2, LogIn, Mail } from "lucide-react";
import { PasswordChangePrompt } from "../components/PasswordChangePrompt";
import {
  AuthApiError,
  completeAccountSetup,
  fetchAccountSetupState,
  type AccountSetupState,
} from "../services/auth";
import { getStripePaymentStatusLabel } from "../services/workflow-ui";

function formatDateTime(value: string | null) {
  if (!value) {
    return null;
  }

  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "long",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return null;
  }
}

export function AccountSetup() {
  const [searchParams] = useSearchParams();
  const [setup, setSetup] = useState<AccountSetupState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const token = searchParams.get("token");
  const acceptedAtLabel = formatDateTime(setup?.orderAcceptedAt || null);
  const loginHref = useMemo(() => {
    if (!setup?.customerEmail) {
      return "/login";
    }

    return `/login?email=${encodeURIComponent(setup.customerEmail)}`;
  }, [setup?.customerEmail]);

  useEffect(() => {
    let isMounted = true;

    const loadSetup = async () => {
      if (!token) {
        if (isMounted) {
          setError("Der Aktivierungslink fehlt oder ist ungültig.");
          setIsLoading(false);
        }
        return;
      }

      try {
        const nextSetup = await fetchAccountSetupState(token);

        if (!isMounted) {
          return;
        }

        setSetup(nextSetup);
        setIsCompleted(nextSetup.mustSetPassword !== true);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(
          loadError instanceof AuthApiError
            ? loadError.message
            : "Der Aktivierungslink konnte nicht geladen werden",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadSetup();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const handleSetupPassword = async (password: string) => {
    if (!token) {
      throw new Error("Der Aktivierungslink fehlt");
    }

    const nextSetup = await completeAccountSetup(token, password);
    setSetup(nextSetup);
    setIsCompleted(true);
  };

  return (
    <div className="pt-20">
      <section className="bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_420px]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-medium text-white">
                <Mail size={16} />
                Konto aktivieren
              </div>

              <div className="space-y-4">
                <h1 className="text-4xl leading-tight text-gray-950 lg:text-5xl">
                  Passwort für Ihren Kundenbereich festlegen
                </h1>
                <p className="max-w-2xl text-lg leading-relaxed text-gray-600">
                  Über diesen Link können Sie Ihren Zugang abschließen und anschließend direkt in
                  Ihren Kundenbereich wechseln.
                </p>
              </div>

              {acceptedAtLabel ? (
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm">
                  <CheckCircle2 size={16} />
                  {setup?.isPaymentConfirmed ? "Zahlung bestätigt am" : "Bestellung übernommen am"}{" "}
                  {acceptedAtLabel}
                </div>
              ) : null}

              {setup && !setup.isPaymentConfirmed ? (
                <div className="max-w-2xl rounded-3xl border border-amber-200 bg-amber-50 p-5 text-sm leading-relaxed text-amber-950">
                  Ihr Auftrag wurde bereits übernommen. Die SEPA-Zahlung ist bei Stripe aber noch
                  in Verarbeitung und noch nicht endgültig auf unserem Konto eingegangen.
                  {setup.paymentStatus ? (
                    <span className="mt-2 block font-semibold">
                      Stripe-Status: {getStripePaymentStatusLabel(setup.paymentStatus)}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-[2rem] border border-gray-200 bg-white p-7 shadow-xl shadow-gray-200/70"
            >
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-4 w-32 rounded-full bg-gray-100" />
                  <div className="h-10 w-full rounded-2xl bg-gray-100" />
                  <div className="h-28 w-full rounded-3xl bg-gray-100" />
                </div>
              ) : error ? (
                <div className="space-y-5">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-red-600">
                      Fehler
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold text-gray-950">
                      Link nicht mehr gültig
                    </h2>
                  </div>
                  <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700">
                    {error}
                  </div>
                  <Link
                    to="/login?setup=1"
                    className="inline-flex w-full items-center justify-center rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                  >
                    Neuen Link anfordern
                  </Link>
                </div>
              ) : setup ? (
                <div className="space-y-6">
                  <div>
                    <p className="text-sm font-semibold uppercase tracking-[0.18em] text-gray-500">
                      Ihr Zugang
                    </p>
                    <h2 className="mt-3 text-2xl font-semibold text-gray-950">
                      {isCompleted ? "Passwort gespeichert" : "Passwort vergeben"}
                    </h2>
                    <p className="mt-3 text-sm leading-relaxed text-gray-600">
                      {setup.customerName} · {setup.customerEmail}
                    </p>
                    {setup.projectTitle ? (
                      <p className="mt-1 text-sm leading-relaxed text-gray-500">
                        Bestellung: {setup.projectTitle}
                      </p>
                    ) : null}
                  </div>

                  {isCompleted ? (
                    <div className="space-y-5">
                      <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="mt-0.5 text-emerald-700" size={22} />
                          <div>
                            <h3 className="text-base font-semibold text-emerald-900">
                              Zugang bereit
                            </h3>
                            <p className="mt-2 text-sm leading-relaxed text-emerald-800">
                              Ihr Passwort wurde gespeichert. Sie können sich jetzt anmelden.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Link
                        to={loginHref}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                      >
                        <LogIn size={16} />
                        Jetzt einloggen
                      </Link>
                    </div>
                  ) : (
                    <>
                      <PasswordChangePrompt
                        title="Passwort festlegen"
                        description="Wählen Sie jetzt Ihr persönliches Passwort für den Kundenbereich."
                        submitLabel="Passwort speichern"
                        onSubmitPassword={handleSetupPassword}
                        onSuccess={() => setIsCompleted(true)}
                      />

                      <div className="rounded-3xl border border-gray-200 bg-gray-50 p-5 text-sm leading-relaxed text-gray-600">
                        Falls Sie den Vorgang unterbrechen, können Sie später jederzeit einen neuen
                        Link über die Login-Seite anfordern.
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
